import React from 'react';

import { ProductDetailsScreen } from 'types/state';
import { useServices } from 'hooks/useServices';
import { useServiceState } from 'hooks/useServiceState';
import { ModalLayerComponent } from 'design/modalLayer';
import { SidebarModalWindowComponent } from 'design/sidebarModal';
import { AsyncBoundaryComponent } from 'utils/asyncBoundary/asyncBoundaryComponents';
import { ProductManagerContent } from './ProductManagerContent';
import { ProductManagerSummary } from './ProductManagerSummary';
import { ProductManagerArchive } from './ProductManagerArchive';
import { ProductManagerProductEditor } from './ProductManagerProductEditor';
import { ProductManagerTariffEditor } from './ProductManagerTariffEditor';

type TProps = {
  productId: number;
  onProductEditorSubmitDidSucceed(): void;
  onTariffEditorSubmitDidSucceed(): void;
  onTariffDeleteDidSucceed(): void;
  onClose(): void;
};

export const ProductManager = (props: TProps) => {
  const {
    productId,
    onClose,
    onProductEditorSubmitDidSucceed,
    onTariffEditorSubmitDidSucceed,
    onTariffDeleteDidSucceed,
  } = props;

  const { getProductDetails, deleteTariff } = useServices();

  const [detailsState, getDetailsWithParams] = useServiceState(
    getProductDetails
  );

  const getDetails = React.useCallback(() => {
    getDetailsWithParams({ productId });
  }, [productId, getDetailsWithParams]);

  const [activeScreen, setActiveScreen] = React.useState<ProductDetailsScreen>(
    'SUMMARY'
  );

  const openSummaryScreen = React.useCallback(() => {
    setActiveScreen('SUMMARY');
  }, []);

  const openArchiveScreen = React.useCallback(() => {
    setActiveScreen('ARCHIVE');
  }, []);

  const openProductEditorScreen = React.useCallback(() => {
    setActiveScreen('PRODUCT_EDITOR');
  }, []);

  const openActiveTariffEditorScreen = React.useCallback(() => {
    setActiveScreen('ACTIVE_TARIFF_EDITOR');
  }, []);

  const openNewTariffEditorScreen = React.useCallback(() => {
    setActiveScreen('NEW_TARIFF_EDITOR');
  }, []);

  const handleArchiveTabClick = React.useCallback(() => {
    openArchiveScreen();
  }, [openArchiveScreen]);

  const handleSummaryTabClick = React.useCallback(() => {
    openSummaryScreen();
  }, [openSummaryScreen]);

  const handleEditProduct = React.useCallback(() => {
    openProductEditorScreen();
  }, [openProductEditorScreen]);

  const handleEditTariff = React.useCallback(() => {
    openActiveTariffEditorScreen();
  }, [openActiveTariffEditorScreen]);

  const handleCreateTariff = React.useCallback(() => {
    openNewTariffEditorScreen();
  }, [openNewTariffEditorScreen]);

  const handleProductEditorCancel = React.useCallback(() => {
    openSummaryScreen();
  }, [openSummaryScreen]);

  const handleDeleteTariff = React.useCallback(
    (tariffId) => {
      deleteTariff({
        tariffId,
      }).then(() => {
        getDetails();
        onTariffDeleteDidSucceed();
      });
    },
    [deleteTariff, getDetails, onTariffDeleteDidSucceed]
  );

  const handleProductEditorSubmitDidSucceed = React.useCallback(() => {
    getDetails();
    onProductEditorSubmitDidSucceed();
    openSummaryScreen();
  }, [getDetails, onProductEditorSubmitDidSucceed, openSummaryScreen]);

  const handleTariffEditorCancel = React.useCallback(() => {
    openSummaryScreen();
  }, [openSummaryScreen]);

  const handleTariffEditorSubmitDidSucceed = React.useCallback(() => {
    getDetails();
    onTariffEditorSubmitDidSucceed();
    openSummaryScreen();
  }, [getDetails, onTariffEditorSubmitDidSucceed, openSummaryScreen]);

  React.useEffect(() => {
    getDetails();
  }, [getDetails]);

  React.useLayoutEffect(() => {
    setActiveScreen('SUMMARY');
  }, [productId]);

  const handleClose = React.useMemo(() => {
    switch (activeScreen) {
      case 'SUMMARY':
      case 'ARCHIVE':
        return onClose;
      default:
        return undefined;
    }
  }, [activeScreen, onClose]);

  return (
    <ModalLayerComponent theme="transparent">
      <SidebarModalWindowComponent
        onMisclick={handleClose}
        onEscape={handleClose}
        onClose={handleClose}
      >
        <AsyncBoundaryComponent fallback={null}>
          {detailsState &&
            detailsState.resolvedWith &&
            (activeScreen === 'SUMMARY' || activeScreen === 'ARCHIVE') && (
              <ProductManagerContent
                details={detailsState.resolvedWith}
                activeScreen={activeScreen}
                onArchiveTabClick={handleArchiveTabClick}
                onSummaryTabClick={handleSummaryTabClick}
              >
                {activeScreen === 'SUMMARY' && (
                  <ProductManagerSummary
                    details={detailsState.resolvedWith}
                    onEditProduct={handleEditProduct}
                    onEditTariff={handleEditTariff}
                    onCreateTariff={handleCreateTariff}
                    onDeleteTariff={handleDeleteTariff}
                  />
                )}
                {activeScreen === 'ARCHIVE' && (
                  <ProductManagerArchive details={detailsState.resolvedWith} />
                )}
              </ProductManagerContent>
            )}
          {detailsState &&
            detailsState.resolvedWith &&
            activeScreen === 'PRODUCT_EDITOR' && (
              <ProductManagerProductEditor
                product={detailsState.resolvedWith.product}
                onCancel={handleProductEditorCancel}
                onSubmitDidSucceed={handleProductEditorSubmitDidSucceed}
              />
            )}
          {detailsState &&
            detailsState.resolvedWith &&
            detailsState.resolvedWith.product.latestTariff &&
            activeScreen === 'ACTIVE_TARIFF_EDITOR' && (
              <ProductManagerTariffEditor
                product={detailsState.resolvedWith.product}
                tariff={detailsState.resolvedWith.product.latestTariff}
                onCancel={handleTariffEditorCancel}
                onSubmitDidSucceed={handleTariffEditorSubmitDidSucceed}
              />
            )}
          {detailsState &&
            detailsState.resolvedWith &&
            activeScreen === 'NEW_TARIFF_EDITOR' && (
              <ProductManagerTariffEditor
                product={detailsState.resolvedWith.product}
                lastTariff={
                  detailsState.resolvedWith.product.latestTariff || undefined
                }
                onCancel={handleTariffEditorCancel}
                onSubmitDidSucceed={handleTariffEditorSubmitDidSucceed}
              />
            )}
        </AsyncBoundaryComponent>
      </SidebarModalWindowComponent>
    </ModalLayerComponent>
  );
};
